import { useState, useEffect, useMemo } from 'react';
// Import Firebase for web environment
import { database as firestore, collection, onSnapshot, query, orderBy } from '../Utils/firebaseConfig.js';
import { useAuth } from './AuthProvider.jsx';

/**
 * Fetches all resources from Firestore. Works in both Electron and web.
 * - Web: Uses Firestore onSnapshot (offline-capable)
 * - Electron: Falls back to IPC if renderer Firestore fails
 * Also updates a global id->name cache for quick lookups in UI (notifications).
 */
function useFirestoreResources() {
  const [resources, setResources] = useState([]);
  const { user } = useAuth();

  useEffect(() => {
    let unsubscribe;
    const listenViaRenderer = () => {
      // Only proceed if user is authenticated
      if (!user) {
        console.log("[useFirestoreResources] No authenticated user, skipping Firebase connection");
        setResources([]);
        return () => {};
      }
      console.log("[useFirestoreResources] Fetching resources via Firebase (renderer)");
      const resourcesQuery = query(collection(firestore, "resources"), orderBy("order"));
      unsubscribe = onSnapshot(resourcesQuery, (snapshot) => {
        const flatResources = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setResources(flatResources);
        // Build a global id->name map for components that need quick lookups (e.g., notifications)
        try {
          const nameMap = {};
          flatResources.forEach(r => { if (r && r.id != null && r.name) nameMap[r.id] = r.name; });
          window.__resourceNameMap = nameMap;
        } catch (_) {}
        console.log(`[useFirestoreResources] Retrieved ${flatResources.length} resources via Firebase (renderer)`);
      }, (error) => {
        console.error("[useFirestoreResources] ❌ Renderer Firestore failed:", error);
        // Fallback to IPC if available
        if (window.electronAPI?.getAllFirestoreData) {
          void fetchViaIpc();
        }
      });
      return () => unsubscribe && unsubscribe();
    };

    const fetchViaIpc = async () => {
      try {
        console.log("[useFirestoreResources] Fetching resources via Electron IPC fallback");
        const result = await window.electronAPI.getAllFirestoreData("resources");
        if (result?.success) {
          setResources(result.data);
          try {
            const nameMap = {};
            (result.data || []).forEach(r => { if (r && r.id != null && r.name) nameMap[r.id] = r.name; });
            window.__resourceNameMap = nameMap;
          } catch (_) {}
          console.log(`[useFirestoreResources] Retrieved ${result.data.length} resources via IPC`);
        } else {
          console.error("[useFirestoreResources] ❌ IPC error:", result?.error);
          setResources([]);
        }
      } catch (error) {
        console.error("[useFirestoreResources] ❌ IPC fetch failed:", error);
        setResources([]);
      }
    };

    // Always try renderer Firestore first (works offline). It will fallback to IPC on error.
    const cleanup = listenViaRenderer();
    return () => {
      if (unsubscribe) unsubscribe();
      if (typeof cleanup === 'function') cleanup();
    };
  }, [user]);

  return resources;
}

/**
 * A master hook that orchestrates all data processing for the calendar.
 * It now fetches the complete resource list from Firestore and reconstructs
 * the required nested tree structure for the calendar.
 */
export default function useCalendarData(allEvents, currentDate) {
  // 1. Fetch the complete, flat list of ALL resources from Firestore.
  const flatResources = useFirestoreResources();
  // 2. Reconstruct the nested tree structure that Mobiscroll requires.
  const allResources = useMemo(() => {
    if (!flatResources.length) return [];
    
    const resourceMap = new Map();
    const finalNestedList = [];

    // First pass: put everything in a map and ensure a 'children' array exists.
    flatResources.forEach(res => {
      resourceMap.set(res.id, { ...res, children: res.children || [] });
    });

    // Second pass: iterate and link children to their parents within the map.
    resourceMap.forEach(res => {
      if (res.parentId && resourceMap.has(res.parentId)) {
        const parent = resourceMap.get(res.parentId);
        // Add the child to the parent, but only if it's not already there.
        if (parent && !parent.children.some(c => c.id === res.id)) {
          parent.children.push(res);
        }
      } else if (!res.parentId) {
        // If a resource has no parentId, it's a top-level item (like "Crew").
        finalNestedList.push(res);
      }
    });
    
    // Sort the final top-level list by the 'order' field.
    return finalNestedList.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [flatResources]);

  // 3. Create a Set of all valid resource IDs for fast filtering.
  const validResourceIds = useMemo(() => {
    const ids = new Set();
    const extractIds = (resources) => {
      resources.forEach(res => {
        ids.add(res.id);
        if (res.children) {
          extractIds(res.children);
        }
      });
    };
    extractIds(allResources);
    return ids;
  }, [allResources]);

  // 4. Filter events to only include those with valid resource assignments.
  const filteredEvents = useMemo(() => {
    return (allEvents || []).filter(event => {
      if (event.visibleInCalendar === false) return false;
      
      const resources = Array.isArray(event.resource) ? event.resource : [event.resource];
      return resources.every(resourceId => validResourceIds.has(resourceId));
    });
  }, [allEvents, validResourceIds]);

  // 5. Generate the dynamic background colors for the calendar.
  const calendarColors = useMemo(() => {
    const colors = [];
    if (!allResources.length) return [];

    const allResourceIds = Array.from(validResourceIds);

    colors.push({
      background: '#FFF3F4',
      recurring: { repeat: 'weekly', weekDays: 'SU' },
    });
    colors.push({
      background: '#f7f2f2',
      recurring: { repeat: 'weekly', weekDays: 'SA' },
    });
    colors.push({
      date: currentDate,
      background: 'rgb(227, 255, 227)',
      resource: allResourceIds,
    });
    
    return colors;
  }, [allResources, currentDate, validResourceIds]);

  // Return everything the calendar component needs.
  return { resources: allResources, filteredEvents, calendarColors };
}

